import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Compass, X } from "@phosphor-icons/react";

function uid() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u?.user_id || u?.id || u?.email || "";
  } catch { return ""; }
}
const doneKey = () => `recoveros_quickstart_done_${uid()}`;
const progressKey = () => `recoveros_quickstart_progress_${uid()}`;
export const quickStartDone = () => { try { return !!localStorage.getItem(doneKey()); } catch { return false; } };
function readProgress() { try { return JSON.parse(localStorage.getItem(progressKey()) || "null"); } catch { return null; } }
function clearProgress() { try { localStorage.removeItem(progressKey()); } catch { /* noop */ } }
function emitUpdated() { window.dispatchEvent(new Event("recoveros:quickstart-updated")); }

const S = (title, description, element, path, side = "bottom", align = "start") => ({ title, description, element, path, side, align });

function buildSteps(role) {
  if (role === "agent") {
    return [
      S("Welcome to RecoverOS Quick Start", "In ~2 minutes you'll see your daily recovery workflow end-to-end. You can skip anytime with ✕.", undefined, "/dashboard"),
      S("Your Dashboard", "Your day at a glance — outstanding, follow-ups due and what needs action first.", '[data-testid="dashboard-kpis"]', "/dashboard", "bottom"),
      S("Today's Follow-ups", "Your Work Queue lists accounts due today, auto-prioritised so you always know who to chase next.", '[data-testid="nav-work-queue"]', "/dashboard", "right"),
      S("Assigned Accounts", "Every account assigned to you lives here. Let's open one.", '[data-testid="nav-accounts"]', "/dashboard", "right"),
      S("Open an Account", "Click any row to open the full borrower profile.", '[data-testid^="account-row-"]', "/accounts", "top"),
      S("Log an Activity", "Record a call, note or contact outcome — it updates the borrower timeline instantly.", '[data-testid="action-note"]', "ACCOUNT_DETAIL", "bottom"),
      S("Promise to Pay", "Captured a commitment? Log a PTP with the promised amount and date so it's tracked automatically.", '[data-testid="action-ptp"]', "ACCOUNT_DETAIL", "bottom"),
      S("Start a Settlement", "Negotiating a settlement? Raise it here for approval — no spreadsheets needed.", '[data-testid="action-settlement"]', "ACCOUNT_DETAIL", "bottom"),
      S("Upload Documents", "Account documents and generated notices live under this tab.", '[data-testid="tab-documents"]', "ACCOUNT_DETAIL", "bottom"),
      S("Schedule Next Follow-up", "Always set the next touchpoint so nothing slips through the cracks.", '[data-testid="action-followup"]', "ACCOUNT_DETAIL", "bottom"),
      S("You're all set!", "Next: work Today's Follow-ups, log every contact and keep PTPs updated. Replay this anytime from the Help (?) menu.", undefined, "/dashboard"),
    ];
  }
  if (role === "manager") {
    return [
      S("Welcome to RecoverOS Quick Start", "A ~2 minute tour of your team's command center. Skip anytime with ✕.", undefined, "/dashboard"),
      S("Team Dashboard", "Live portfolio KPIs across your team — outstanding, recovered and follow-ups.", '[data-testid="dashboard-kpis"]', "/dashboard", "bottom"),
      S("Assigned Accounts", "View and assign accounts to your agents from the Accounts module.", '[data-testid="nav-accounts"]', "/dashboard", "right"),
      S("Agent Performance", "Track every agent's recovery and activity from Reports.", '[data-testid="nav-reports"]', "/dashboard", "right"),
      S("Recovery Health Score", "A single grade for your portfolio's recovery performance, with the drivers behind it.", '[data-testid="health-card"]', "/dashboard", "top"),
      S("Portfolio Data Health", "RecoverOS auto-calculates DPD from each account's Payment Due Date. This panel shows your Import Quality Score and flags 'Unknown DPD' accounts you can export and fix.", '[data-testid="data-health-panel"]', "/dashboard", "top"),
      S("Reports", "Export aging, collection efficiency and recovery-trend reports for reviews.", '[data-testid="nav-reports"]', "/dashboard", "right"),
      S("Daily Work Queue", "Your team's prioritised daily worklist — who to chase first, every morning.", '[data-testid="nav-work-queue"]', "/dashboard", "right"),
      S("Audit Logs", "Every action is logged for compliance — fully traceable here.", '[data-testid="nav-audit-log"]', "/dashboard", "right"),
      S("You're all set!", "Next: assign accounts, review agent performance and check the daily work queue. Replay anytime from Help (?).", undefined, "/dashboard"),
    ];
  }
  return [
    S("Welcome to RecoverOS Quick Start", "In ~2 minutes you'll see how to run collections end-to-end. You can skip anytime with ✕.", undefined, "/dashboard"),
    S("Dashboard Overview", "Your executive command center — live KPIs across the whole portfolio.", '[data-testid="dashboard-kpis"]', "/dashboard", "bottom"),
    S("Money Stuck Analyzer™", "See exactly how much is recoverable, at risk or stuck — and what to do next.", '[data-testid="management-summary"]', "/dashboard", "bottom"),
    S("Recovery Health Score", "A single score grading your receivables performance, with the factors driving it.", '[data-testid="health-card"]', "/dashboard", "top"),
    S("Portfolio Data Health", "Your Import Quality Score. RecoverOS auto-calculates DPD from each account's Payment Due Date — this panel flags any 'Unknown DPD' accounts and lets you export & fix them to boost Money Stuck™ and Health Score accuracy.", '[data-testid="data-health-panel"]', "/dashboard", "top"),
    S("Accounts Module", "Every account lives here — search, filter, assign and open them. Let's open one.", '[data-testid="nav-accounts"]', "/dashboard", "right"),
    S("Open an Account", "Click any row to open the full borrower profile.", '[data-testid^="account-row-"]', "/accounts", "top"),
    S("Log First Activity", "Record a call, note or outcome — it updates the borrower timeline instantly.", '[data-testid="action-note"]', "ACCOUNT_DETAIL", "bottom"),
    S("Schedule First Follow-up", "Set the next touchpoint so your team never loses momentum on an account.", '[data-testid="action-followup"]', "ACCOUNT_DETAIL", "bottom"),
    S("Reports", "Export aging, collection efficiency and recovery trends for management reviews.", '[data-testid="nav-reports"]', "ACCOUNT_DETAIL", "right"),
    S("Team Management", "Invite managers and agents, set roles and reset passwords from Users.", '[data-testid="nav-users"]', "ACCOUNT_DETAIL", "right"),
    S("You're all set!", "Next: invite your team, assign accounts and review the Money Stuck Analyzer daily. Replay anytime from Help (?).", undefined, "/dashboard"),
  ];
}

function isOnPath(stepPath, pathname) {
  if (!stepPath) return true;
  if (stepPath === "ACCOUNT_DETAIL") return pathname.startsWith("/accounts/");
  return pathname === stepPath;
}

export default function QuickStart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role;
  const eligible = ["client_admin", "manager", "agent"].includes(role);
  const acctId = useRef(null);
  const startedRef = useRef(false);
  const driverRef = useRef(null);

  const resolvePath = useCallback((p) => {
    if (p === "ACCOUNT_DETAIL") return acctId.current ? `/accounts/${acctId.current}` : "/accounts";
    return p || "/dashboard";
  }, []);

  const startTour = useCallback(async (startIndex = 0) => {
    if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* noop */ } }
    try {
      const r = await api.get("/accounts", { params: { limit: 1 } });
      acctId.current = r.data?.items?.[0]?.account_id || null;
    } catch { acctId.current = null; }

    const stepDefs = buildSteps(role);
    const total = stepDefs.length;
    const steps = stepDefs.map((s) => ({
      element: s.element,
      _path: s.path,
      popover: { title: s.title, description: s.description, side: s.side, align: s.align },
    }));

    const saveProgress = (index) => {
      try { localStorage.setItem(progressKey(), JSON.stringify({ index, total, ts: Date.now() })); } catch { /* noop */ }
      emitUpdated();
    };
    const markFinished = () => {
      try { localStorage.setItem(doneKey(), "1"); } catch { /* noop */ }
      clearProgress();
      emitUpdated();
    };

    const navAndMove = (idx, move) => {
      const target = steps[idx];
      if (target && !isOnPath(target._path, window.location.pathname)) {
        navigate(resolvePath(target._path));
        setTimeout(move, target._path === "ACCOUNT_DETAIL" ? 1100 : 650);
      } else {
        move();
      }
    };

    const d = driver({
      showProgress: true,
      allowClose: true,
      overlayColor: "rgba(15,23,42,0.55)",
      stagePadding: 6,
      stageRadius: 6,
      popoverClass: "recoveros-qs",
      progressText: "{{current}} of {{total}}",
      nextBtnText: "Next →",
      prevBtnText: "← Previous",
      doneBtnText: "Finish",
      steps,
      onHighlighted: () => { saveProgress(d.getActiveIndex()); },
      onNextClick: () => {
        const cur = d.getActiveIndex();
        if (cur >= total - 1) { markFinished(); d.destroy(); return; }
        navAndMove(cur + 1, () => d.moveNext());
      },
      onPrevClick: () => navAndMove(d.getActiveIndex() - 1, () => d.movePrevious()),
      onCloseClick: () => { d.destroy(); }, // skip: keep saved progress so the resume chip appears
      onDestroyed: () => { driverRef.current = null; emitUpdated(); },
    });
    driverRef.current = d;

    const firstPath = steps[startIndex]?._path;
    const launch = () => d.drive(startIndex);
    if (firstPath && !isOnPath(firstPath, window.location.pathname)) {
      navigate(resolvePath(firstPath));
      setTimeout(launch, firstPath === "ACCOUNT_DETAIL" ? 1100 : 550);
    } else {
      setTimeout(launch, 250);
    }
  }, [role, navigate, resolvePath]);

  // Auto-start ONCE — only for users who have never started (no progress, not done).
  useEffect(() => {
    if (!eligible || startedRef.current) return;
    if (quickStartDone() || readProgress()) return;
    if (window.location.pathname !== "/dashboard") return;
    if (!localStorage.getItem(`recoveros_excel_uploaded_${uid()}`)) return;
    startedRef.current = true;
    const t = setTimeout(() => startTour(0), 900);
    return () => clearTimeout(t);
  });

  // Replay (from Help, fresh) or Resume (from chip, saved index).
  useEffect(() => {
    const h = (e) => {
      if (e?.detail?.resume) {
        const p = readProgress();
        startTour(p?.index || 0);
      } else {
        try { localStorage.removeItem(doneKey()); } catch { /* noop */ }
        clearProgress();
        startTour(0);
      }
    };
    window.addEventListener("recoveros:start-quickstart", h);
    return () => window.removeEventListener("recoveros:start-quickstart", h);
  }, [startTour]);

  useEffect(() => () => { if (driverRef.current) { try { driverRef.current.destroy(); } catch { /* noop */ } } }, []);

  return null;
}

// Subtle resume nudge for users who skipped the tour midway. Renders nothing if finished or never started.
export function QuickStartChip() {
  const { user } = useAuth();
  const eligible = ["client_admin", "manager", "agent"].includes(user?.role);
  const [prog, setProg] = useState(() => readProgress());
  useEffect(() => {
    const h = () => setProg(readProgress());
    window.addEventListener("recoveros:quickstart-updated", h);
    return () => window.removeEventListener("recoveros:quickstart-updated", h);
  }, []);
  if (!eligible || quickStartDone() || !prog) return null;
  const left = Math.max(0, (prog.total || 0) - ((prog.index || 0) + 1));
  if (left <= 0) return null;
  const dismiss = (e) => { e.stopPropagation(); try { localStorage.setItem(doneKey(), "1"); } catch { /* noop */ } clearProgress(); setProg(null); };
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent("recoveros:start-quickstart", { detail: { resume: true } }))}
      className="mb-4 inline-flex items-center gap-2 rounded-sm border border-[#0B3D91]/25 bg-[#0B3D91]/5 px-3 py-1.5 text-xs text-[#0B3D91] hover:bg-[#0B3D91]/10 transition-colors"
      data-testid="quickstart-resume-chip"
    >
      <Compass size={15} weight="bold" />
      <span className="font-semibold">Resume Quick Start</span>
      <span className="text-[#0B3D91]/70">— {left} of {prog.total} step{prog.total === 1 ? "" : "s"} left</span>
      <span onClick={dismiss} className="ml-1 text-[#0B3D91]/50 hover:text-[#0B3D91]" data-testid="dismiss-quickstart-chip" aria-label="Dismiss">
        <X size={13} weight="bold" />
      </span>
    </button>
  );
}
